import { BaseModel } from '../global-services/base.model';
import { Organization } from './user-organization.model';
import { PRODUCT_TYPES } from '../shared/utils';
import { fromUnixTime, getTime, isBefore, subDays } from 'date-fns';

export enum PLAN_TYPES {
    Trial = 'TR',
    Starter = 'ST',
    Basic = 'BA',
    Premium = 'PR',
    Reseller = 'RE',
    Umbrella = 'UM',
    WhiteLabel = 'WL',
    UmbrellaReseller = 'UR',
    Enterprise = 'EN',
    BusinessPlus = 'BP',
    EnterpriseAhead = 'EA',
    EnterpriseAccelerate = 'EC',
    EnterpriseUnlimited = 'EU',
    Lite = 'LT',
    Pro = 'PO',
    Plus = 'PL',
    Advanced = 'AD',
    Solo = 'SL',
    Team = 'TM',
    Business = 'BN',
    DBCEnterprise = 'DE',
    DBCBusinessPlus = 'DB',
    DBCFreemium = 'DF',
    DBCNTeam = 'NT',
}

export enum USER_GROUPS {
    Owner = 'OW',
    Admin = 'AD',
    SandBox = 'SB',
    ReadOnly = 'RO',
}

export enum SUBSCRIPTION_STATE {
    Active = 'A',
    Expired = 'E',
    Removed = 'R',
}

export class UserGroup extends BaseModel {
    organization: number;
    organization__name: string;
    user_group: USER_GROUPS;
    cards_permission: boolean;
    qr_permission: boolean;
    // permissions object is required for server configuration
    permissions: {
        qr: boolean;
        cards: boolean;
    };

    constructor(json?: any) {
        super();
        if (!json) {
            return;
        }
        this.organization = json['organization'];
        this.organization__name = json['organization__name'];
        this.user_group = json['user_group'];
        this.cards_permission = json['cards_permission'];
        this.qr_permission = json['qr_permission'];
        this.permissions = {
            qr: this.qr_permission,
            cards: this.cards_permission,
        };
        // Remove qr or cards from permissions
        if (!this.cards_permission) {
            delete this.permissions.cards;
        } else if (!this.qr_permission) {
            delete this.permissions.qr;
        }
    }
}

export class SubscriptionInfo extends BaseModel {
    plan: PLAN_TYPES;
    state: SUBSCRIPTION_STATE;
    status: string;

    constructor(json?: any) {
        super();
        this.plan = json['plan'] ? json['plan'] : null;
        this.state = json['state'] ? json['state'] : null;
        this.status = json['status'] ? json['status'] : '';
    }
}

export class Subscription extends BaseModel {
    dbc: SubscriptionInfo;
    qr: SubscriptionInfo;

    constructor(json?: any) {
        super();
        this.dbc = json['dbc'] ? new SubscriptionInfo(json['dbc']) : null;
        this.qr = json['qr'] ? new SubscriptionInfo(json['qr']) : null;
    }
}

export class User extends BaseModel {
    id: number;
    username: string;
    email: string;
    email_verified: string;
    billing_email: string;
    first_name: string;
    last_name: string;
    user_group: string;
    customer_plan: PLAN_TYPES;
    date_joined: Date;
    allow_first_login: boolean;
    subscription_state: SUBSCRIPTION_STATE;
    paywhirl_customer_id: string;
    shopify_id: string;
    stripe_id: string;
    firebase_token: string;
    profile_picture: string;
    organization: Organization;
    meta: any;
    timezone: string;
    weekly_analytics: boolean;
    monthly_analytics: boolean;
    org_user_groups: Array<UserGroup> = [];
    delete_organizations = [];
    designation: string;
    phone_number: string;
    subscription: Subscription;

    // Used for PWA cache eviction policy
    __last_login_epoch__: number;

    static getReadablePlan(plan: PLAN_TYPES): string {
        switch (plan) {
            case PLAN_TYPES.Basic:
                return 'Basic';
            case PLAN_TYPES.Premium:
                return 'Premium';
            case PLAN_TYPES.Reseller:
                return 'Reseller';
            case PLAN_TYPES.Umbrella:
                return 'Umbrella';
            case PLAN_TYPES.WhiteLabel:
                return 'Whitelabel';
            case PLAN_TYPES.Enterprise:
                return 'Enterprise Activate';
            case PLAN_TYPES.BusinessPlus:
                return 'Business+';
            case PLAN_TYPES.EnterpriseAhead:
                return 'Enterprise Ahead';
            case PLAN_TYPES.EnterpriseAccelerate:
                return 'Enterprise Accelerate';
            case PLAN_TYPES.EnterpriseUnlimited:
                return 'Enterprise Unlimited';
            case PLAN_TYPES.UmbrellaReseller:
                return 'Umbrella Reseller';
            case PLAN_TYPES.Trial:
                return 'Trial';
            case PLAN_TYPES.Starter:
                return 'Starter';
            case PLAN_TYPES.Lite:
                return 'Lite';
            case PLAN_TYPES.Pro:
                return 'Pro';
            case PLAN_TYPES.Plus:
                return 'Plus';
            case PLAN_TYPES.Advanced:
                return 'Advanced';
            case PLAN_TYPES.Solo:
                return 'Solo';
            case PLAN_TYPES.Team:
                return 'Team';
            case PLAN_TYPES.Business:
                return 'Business';
            case PLAN_TYPES.DBCEnterprise:
                return 'Enterprise DBC';
            case PLAN_TYPES.DBCBusinessPlus:
                return 'Business+ (DBC)';
            case PLAN_TYPES.DBCFreemium:
                return 'Free plan'
            case PLAN_TYPES.DBCNTeam:
                return 'Team (DBC)'
            default:
                return '';
        }
    }
    constructor(json: any) {
        super();
        this.id = json['id'];
        this.username = json['username'];
        this.email = json['email'];
        this.email_verified = json['email_verified'];
        this.billing_email = json['billing_email'];
        this.first_name = json['first_name'];
        this.last_name = json['last_name'];
        this.user_group = json['user_group'];
        this.customer_plan = json['customer_plan'];
        this.date_joined = json['date_joined']
            ? new Date(json['date_joined'])
            : null;
        this.allow_first_login = json['allow_first_login'];
        this.subscription_state = json['subscription_state'];
        this.shopify_id = json['shopify_id'];
        this.stripe_id = json['stripe_id'];
        this.firebase_token = json['firebase_token'];
        this.paywhirl_customer_id = json['paywhirl_customer_id'];
        this.profile_picture = json['profile_picture'];
        this.organization = new Organization(json['organization']);
        this.meta = json['meta'] || {};
        this.timezone = json['timezone'] || 'UTC';
        this.weekly_analytics = json['weekly_analytics'] || false;
        this.monthly_analytics = json['monthly_analytics'] || false;
        this.phone_number = json['phone_number'];
        this.designation = json['designation'];
        this.subscription = json['subscription'];
        for (const userGroup of json['org_user_groups'] || []) {
            this.org_user_groups.push(new UserGroup(userGroup));
        }
        if (json['delete_organizations']) {
            this.delete_organizations = json['delete_organizations'];
        }
        this.subscription = json['subscription']
            ? new Subscription(json['subscription'])
            : null;
        // TODO: Handle in better way
        if (json['subscription'] && json['subscription'].qr) {
            this.customer_plan = json['subscription'].qr.plan;
            this.subscription_state = json['subscription'].qr.state;
        }

        this.__last_login_epoch__ =
            json['__last_login_epoch__'] || getTime(new Date());
    }

    get isLoggedInBefore90Days(): boolean {
        if (!this.__last_login_epoch__) {
            return false;
        }

        const lastLoginDate = fromUnixTime(this.__last_login_epoch__); // Convert epoch to Date
        const ninetyDaysAgo = subDays(new Date(), 90); // Get date 90 days ago

        return isBefore(lastLoginDate, ninetyDaysAgo); // Returns true if last login is older
    }

    get avtarName() {
        return (
            this.first_name.charAt(0).toUpperCase() +
            this.last_name.charAt(0).toUpperCase()
        );
    }

    getReadableGroup(): string {
        switch (this.user_group) {
            case USER_GROUPS.Admin:
                return 'Administrator';
            case USER_GROUPS.Owner:
                return 'Owner';
            case USER_GROUPS.ReadOnly:
                return 'Viewer';
            case USER_GROUPS.SandBox:
                return 'Editor';
            default:
                return '';
        }
    }
    get mainOrganization() {
        return this.organization.parent || this.organization.id;
    }

    isSuperAdmin(): boolean {
        return (
            this.user_group === USER_GROUPS.Owner ||
            (this.user_group === USER_GROUPS.Admin && !this.organization.parent)
        );
    }

    isAdmin(currentOrgId): boolean {
        return this.isSuperAdmin() || this.org_user_groups.filter(res => res.organization === currentOrgId)[0]?.user_group === USER_GROUPS.Admin;
    }

    isSuperEditor(): boolean {
        return (
            this.user_group === USER_GROUPS.SandBox && !this.organization.parent
        );
    }

    isSuperViewer(): boolean {
        return (
            this.user_group === USER_GROUPS.ReadOnly &&
            !this.organization.parent
        );
    }

    isOnTrialPlan(product: PRODUCT_TYPES = PRODUCT_TYPES.DBC) {
        if (product === PRODUCT_TYPES.QR) {
            return this.qrCustomerPlan === PLAN_TYPES.Trial;
        } else if (product === PRODUCT_TYPES.DBC) {
            return this.subscription.dbc
                ? this.subscription.dbc.status === 'trialing'
                : false;
        }

        return false;
    }

    get qrCustomerPlan(): PLAN_TYPES {
        return this.subscription?.qr?.plan ? this.subscription.qr.plan : null;
    }

    get dbcCustomerPlan(): PLAN_TYPES {
        return this.subscription?.dbc?.plan ? this.subscription.dbc.plan : null;
    }

    getReadablePlan(product: PRODUCT_TYPES = PRODUCT_TYPES.DBC): string {
        if (product === PRODUCT_TYPES.DBC) {
            return User.getReadablePlan(this.dbcCustomerPlan);
        } else {
            return User.getReadablePlan(this.qrCustomerPlan);
        }
    }

    toJSON(): object {
        return {
            id: this.id,
            username: this.username,
            email: this.email,
            email_verified: this.email_verified,
            billing_email: this.billing_email,
            first_name: this.first_name,
            last_name: this.last_name,
            user_group: this.user_group,
            customer_plan: this.customer_plan,
            date_joined: this.date_joined?.toISOString(),
            allow_first_login: this.allow_first_login,
            subscription_state: this.subscription_state,
            shopify_id: this.shopify_id,
            stripe_id: this.stripe_id,
            paywhirl_customer_id: this.paywhirl_customer_id,
            firebase_token: this.firebase_token,
            profile_picture: this.profile_picture,
            organization: this.organization?.toJSON(),
            meta: this.meta,
            timezone: this.timezone,
            weekly_analytics: this.weekly_analytics,
            monthly_analytics: this.monthly_analytics,
            org_user_groups: this.org_user_groups,
            delete_organizations: this.delete_organizations,
            designation: this.designation,
            phone_number: this.phone_number,
            subscription: this.subscription,
            __last_login_epoch__: this.__last_login_epoch__,
        };
    }

    override toString(): string {
        return JSON.stringify(this.toJSON());
    }
}
